import { z } from 'zod';

import { Currency } from './organization-billing';
import { clerkProviderAuthIdSchema, defaultNullish, mongoIdSchema } from './shared';

export enum UserNotificationEvent {
  ArticleGenerated = 'article_generated',
  NewsInbox = 'news_inbox',
}

export enum UserNotificationChannel {
  Email = 'email',
  InApp = 'inapp',
}

// we dont want the password inside the user create because it is handled
// by the third party auth service
// NOTE: password validator is used in the frontend
// TODO: maybe we should pass it there
export const passwordValidator = z.string().min(6, 'PASSWORD_MINIMUM');

export const onboardingOriginValues = [
  'google_search',
  'linkedin',
  'chatgpt_ai',
  'events',
  'word_of_mouth',
  'meta',
  'youtube',
  'other',
] as const;
export type OnboardingOrigin = (typeof onboardingOriginValues)[number];

export const userCreateSchema = z.object({
  firstName: defaultNullish(z.string().nullish().default('')),
  lastName: defaultNullish(z.string().nullish().default('')),
  email: z.string().email('USER_INVALID_EMAIL'),
  language: defaultNullish(z.string().min(2, 'USER_INVALID_LANGUAGE').default('en')),
  selectedOrganizationId: defaultNullish(z.string().default('')),
  selectedBrandId: defaultNullish(z.string().default('')),

  // utm data will be passed in as utm_source / utm_medium
  signupSource: defaultNullish(z.string().default('unknown')).nullish(),
  signupMedium: defaultNullish(z.string().default('unknown')).nullish(),
  signupCampaign: defaultNullish(z.string().default('unknown')).nullish(),
  signupTerm: defaultNullish(z.string().default('unknown')).nullish(),
  signupIpAddress: z.string().nullish(),
  signupCountryCode: z.string().nullish(),
  signupCurrency: z.nativeEnum(Currency).nullish(),

  // ad platform click IDs captured at signup
  signupGclid: defaultNullish(z.string().default('')).nullish(),
  signupFbclid: defaultNullish(z.string().default('')).nullish(),
  signupLiFatId: defaultNullish(z.string().default('')).nullish(),

  // onboarding origin - how did the user hear about us
  onboardingOrigin: z.enum(onboardingOriginValues).nullish(),
  onboardingOriginOther: z.string().nullish(),

  // a way for us to understand if the user has just created his first org
  isntFirstOrg: defaultNullish(z.boolean().default(false)).nullish(),

  // LEGACY: no longer in use
  currency: defaultNullish(z.nativeEnum(Currency).default(Currency.Eur).nullish()),

  notificationPreferences: z
    .object({
      // global channels enable
      channels: z.object({
        [UserNotificationChannel.Email]: z.boolean(),
        [UserNotificationChannel.InApp]: z.boolean(),
      }),

      // per-event channels
      events: z
        .object({
          [UserNotificationEvent.ArticleGenerated]: z.object({
            [UserNotificationChannel.Email]: z.boolean(),
            [UserNotificationChannel.InApp]: z.boolean(),
          }),
          [UserNotificationEvent.NewsInbox]: z
            .object({
              [UserNotificationChannel.Email]: z.boolean(),
              [UserNotificationChannel.InApp]: z.boolean(),
            })
            .nullish(),
        })
        .strip(),
    })
    .nullish(),
});
export type UserCreate = z.infer<typeof userCreateSchema>;

export const userCreateWithProviderSchema = z
  .object({
    providerAuthId: clerkProviderAuthIdSchema,
  })
  .merge(userCreateSchema);
export type UserCreateWithProvider = z.infer<typeof userCreateWithProviderSchema>;

export const userSchema = z
  .object({
    _id: mongoIdSchema,
    createdAt: z.number(),
    updatedAt: z.number(),
    isArchived: z.boolean().nullish(),
  })
  .merge(userCreateWithProviderSchema)
  .strip();

export type User = z.infer<typeof userSchema>;
